/**
 * **The locales, and which one a request speaks** (docs/I18N.md). Two today:
 * English, the source catalog, and German. The locale is never in the URL; it
 * is resolved per request from the person's account setting, else the
 * browser's `Accept-Language` header, else English.
 */

export const LOCALES = ['en', 'de'] as const;

export type Locale = (typeof LOCALES)[number];

/** Each locale's name in its own language, the way a language picker lists
 *  them: an endonym reads the same whatever the page speaks. */
export const LOCALE_NAMES: { readonly [L in Locale]: string } = { en: 'English', de: 'Deutsch' };

/** The locale a request falls back to when nothing names a supported one. */
export const DEFAULT_LOCALE: Locale = 'en';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * The locale of one request. Pure.
 *
 * - `setting`: the person's account setting (`persons.locale`); `null` means
 *   "follow the browser". A value that is not a supported locale is ignored.
 * - `acceptLanguage`: the raw header. Its entries are ranked by `q` (equal
 *   weights keep header order), and the first whose primary subtag is a
 *   supported locale wins: `de-AT` speaks `de`. A `q=0` entry is a refusal and
 *   never matches.
 */
export function resolveLocale(input: {
  setting?: string | null;
  acceptLanguage?: string | null;
}): Locale {
  if (isLocale(input.setting)) return input.setting;
  for (const tag of rankedLanguages(input.acceptLanguage ?? '')) {
    const primary = tag.split('-')[0]!.toLowerCase();
    if (isLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

/** The header's language tags, highest `q` first, `q=0` dropped. */
function rankedLanguages(header: string): string[] {
  const entries: { tag: string; q: number; at: number }[] = [];
  header.split(',').forEach((part, at) => {
    const [rawTag, ...params] = part.trim().split(';');
    const tag = rawTag?.trim() ?? '';
    if (!tag || tag === '*') return;
    let q = 1;
    for (const param of params) {
      const [name, value] = param.trim().split('=');
      if (name?.trim() === 'q') {
        const parsed = Number(value);
        q = Number.isFinite(parsed) ? parsed : 0;
      }
    }
    if (q > 0) entries.push({ tag, q, at });
  });
  return entries.sort((a, b) => b.q - a.q || a.at - b.at).map((e) => e.tag);
}

/**
 * The BCP 47 tag a date formats in, on the client: the browser's own language
 * when it speaks the page's locale (so `en-GB` keeps its day-month order on an
 * English page), the page's locale otherwise (a German page in an English
 * browser formats German dates).
 */
export function dateLocaleFor(locale: Locale, browserLanguages: readonly string[]): string {
  return browserLanguages.find((tag) => tag.split('-')[0]!.toLowerCase() === locale) ?? locale;
}
